import React from 'react'
import { FormTextArea, Message, SemanticCOLORS, Statistic } from 'semantic-ui-react'
import { WithTranslation, withTranslation } from 'react-i18next'
import FieldWrapper from '@cbgms/components/form/FieldWrapper'
import Label from './Label'
import { compose } from '@cbgms/base/utils/compose'
import { WrappedFieldProps } from 'redux-form'

interface ISMSTextAreaProps {
  label: string

  [key: string]: any
}

interface Props extends ISMSTextAreaProps, WithTranslation, WrappedFieldProps {}

const enhance = compose<Props, ISMSTextAreaProps>(withTranslation(), FieldWrapper)

export class SMSTextArea extends React.Component<Props> {
  MAX_NUMBER_CHARS_PER_SMS = 160
  MAX_NUMBER_MESSAGES_PER_SMS = 3

  calculateMessageCount = (messageLength: number) => {
    return messageLength > 0 ? Math.floor((messageLength - 1) / this.MAX_NUMBER_CHARS_PER_SMS) + 1 : 1
  }

  setMessageWarning = (messageLength: number) => {
    const { t } = this.props
    const currentMessageCount = this.calculateMessageCount(messageLength)
    const currentMessageWarning =
      currentMessageCount > 1 ? t('global.sms_message_count_warning', { size: currentMessageCount.toString() }) : ''

    return currentMessageWarning
  }

  setCounterColor = (messageLength: number) => {
    const charsLeft = this.MAX_NUMBER_CHARS_PER_SMS - ((messageLength - 1) % this.MAX_NUMBER_CHARS_PER_SMS) - 1
    const statusColor = charsLeft > 20 ? 'green' : charsLeft > 0 ? 'orange' : 'red'

    return statusColor
  }

  setMaxReached = (messageLength: number) => {
    return messageLength >= this.MAX_NUMBER_MESSAGES_PER_SMS * this.MAX_NUMBER_CHARS_PER_SMS
  }

  calculateCounters = (messageLength: number) => {
    return `${messageLength}` + '/' + `${this.MAX_NUMBER_CHARS_PER_SMS * this.calculateMessageCount(messageLength)}`
  }

  render() {
    const { input, label, meta, t, i18n, tReady, ...props } = this.props

    const error = Boolean(meta.touched && meta.error)

    const smsMessageLength = input.value.length
    const counters = this.calculateCounters(smsMessageLength)
    const counterColor = this.setCounterColor(smsMessageLength)
    const warning = this.setMessageWarning(smsMessageLength)
    const warningMaxReached = this.setMaxReached(smsMessageLength)

    return (
      <div className='field'>
        <Label touched={meta.touched} error={meta.error} content={label} className='d-inline-block' htmlFor={input.name} />
        <Statistic data-testid='sms-textarea-statistic' size='mini' className='mb1' floated='right' color={counterColor as SemanticCOLORS}>
          <Statistic.Value>{counters}</Statistic.Value>
        </Statistic>
        {!warningMaxReached && warning && <Message data-testid='sms-textarea-message' info icon='warning circle' content={warning} />}
        {warningMaxReached && (
          <Message data-testid='sms-textarea-message' positive icon='info circle' content={t('global.sms_message_max_reached')} />
        )}
        <FormTextArea
          maxLength={this.MAX_NUMBER_CHARS_PER_SMS * this.MAX_NUMBER_MESSAGES_PER_SMS}
          error={error}
          required
          id={input.name}
          {...input}
          {...props}
        />
      </div>
    )
  }
}

export default enhance(SMSTextArea)
